import React, { useCallback, useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import useSavImport, { activeConflict, canRunImport, SavImportMode } from '../hooks/useSavImport'
import { useDisplayNames } from '../utils/displayNames'
import type { SavPlannedVariable } from '../types/gotypes'
import SavPlanMessages from '../components/savimport/SavPlanMessages'

const steps = ['Job', 'Upload', 'Review', 'Import']

/** The SAV import staging container is not a job (goapi savimport.IsReservedJobName). */
const RESERVED_JOB_NAME = 'platinum-imports'

const STRATEGY_LABELS: Record<string, { label: string; title: string }> = {
  spss: { label: 'SPSS codes', title: 'The SPSS value codes (0 and negative whole numbers included) are kept as the codes' },
  renumbered: { label: 'Renumbered', title: 'A labelled value is not a whole number, so the codes are renumbered 1..n' },
  generated: { label: 'Generated', title: 'Codes are generated from the distinct values (or a blend)' },
  none: { label: 'No codes', title: 'A numeric variable without value labels' },
}

/** Variables shown in a report/review table before the rest are summarised. */
const CODE_PREVIEW = 4

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const codesSummary = (v: SavPlannedVariable) => {
  const codes = v.codes ?? []
  if (codes.length === 0) return '—'
  const shown = codes.slice(0, CODE_PREVIEW).map((c) => `${c.code} ${c.label}`).join(', ')
  return codes.length > CODE_PREVIEW ? `${shown} … (${codes.length} codes)` : shown
}

const SavImportPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const customer = router.params.customer || account.customer?.name || ''
  const customerLabel = useDisplayNames(customer).customer
  const currentJob = account.job?.name || ''

  const [activeStep, setActiveStep] = useState(0)
  const [destination, setDestination] = useState<'existing' | 'new'>(currentJob ? 'existing' : 'new')
  const [existingMode, setExistingMode] = useState<'add' | 'topup'>('add')
  const [newJobName, setNewJobName] = useState('')
  const [newJobDisplayName, setNewJobDisplayName] = useState('')
  const [savFile, setSavFile] = useState<File | null>(null)
  const [blnFile, setBlnFile] = useState<File | null>(null)
  /** The files the current inspect result belongs to. */
  const [inspectedFiles, setInspectedFiles] = useState<{ sav: File; bln: File | null } | null>(null)
  const [overwriteExisting, setOverwriteExisting] = useState(false)

  const { state, inspect, run, reset } = useSavImport(customer)

  const mode: SavImportMode = destination === 'new' ? 'new' : existingMode
  const jobName = destination === 'existing' ? currentJob : newJobName.trim()
  const conflict = activeConflict(state, mode, jobName)
  const inspectCurrent =
    !!inspectedFiles && inspectedFiles.sav === savFile && inspectedFiles.bln === blnFile && !!state.inspect

  const jobNameError =
    destination === 'new' && newJobName.trim().toLowerCase() === RESERVED_JOB_NAME
      ? `"${RESERVED_JOB_NAME}" is reserved and cannot be used as a job name`
      : ''

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    for (const file of Array.from(e.dataTransfer.files)) {
      if (file.name.endsWith('.sav') || file.name.endsWith('.spss')) {
        setSavFile(file)
      } else if (file.name.endsWith('.bln')) {
        setBlnFile(file)
      }
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleInspect = async () => {
    if (!savFile) return
    if (inspectCurrent && state.stage === 'reviewing') {
      setActiveStep(2)
      return
    }
    setOverwriteExisting(false)
    const ok = await inspect({ file: savFile, blnFile: blnFile || undefined })
    if (ok) {
      setInspectedFiles({ sav: savFile, bln: blnFile })
      setActiveStep(2)
    }
  }

  const handleImport = async () => {
    const queued = await run({
      mode,
      jobName,
      jobDisplayName: mode === 'new' && newJobDisplayName ? newJobDisplayName : undefined,
      customerId: mode === 'new' ? account.customer?.id || undefined : undefined,
      overwriteExisting: mode === 'add' ? overwriteExisting : undefined,
    })
    if (queued) setActiveStep(3)
  }

  const handleOpenVariables = () => {
    const job = state.run?.jobName || jobName
    if (state.run?.mode === 'new') {
      account.addJobToSession(customer, job, newJobDisplayName || job)
    }
    account.syncJob(job)
    router.navigate('variables', { customer, job })
  }

  const handleStartAgain = () => {
    reset()
    setInspectedFiles(null)
    setSavFile(null)
    setBlnFile(null)
    setOverwriteExisting(false)
    setActiveStep(0)
  }

  const handleBack = () => {
    if (activeStep === 0) {
      if (currentJob) {
        router.navigate('variables', { customer, job: currentJob })
      } else {
        account.openCustomerPage('admin')
      }
      return
    }
    setActiveStep((prev) => prev - 1)
  }

  const canProceedJob = destination === 'existing' ? !!currentJob : !!newJobName.trim() && !jobNameError
  const canImport = canProceedJob && canRunImport(state, mode, overwriteExisting, jobName)

  const modeDescription = useMemo(() => {
    if (mode === 'new') return 'A new job will be created and every variable in the file imported into it.'
    if (mode === 'add') return `The file's variables will be added to ${currentJob}. The file must have the same cases as the job.`
    return `The file's cases will be appended to ${currentJob} as a new wave. Variables the file does not carry are padded as missing.`
  }, [mode, currentJob])

  const renderJobStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <RadioGroup value={destination} onChange={(e) => setDestination(e.target.value as 'existing' | 'new')}>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <FormControlLabel
              value="new"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="subtitle1">Create a new job</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a new job and import the file into it
                  </Typography>
                </Box>
              }
            />
            {destination === 'new' && (
              <Box sx={{ mt: 2, ml: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="ID"
                  helperText={jobNameError || 'System name for the job'}
                  error={!!jobNameError}
                  value={newJobName}
                  onChange={(e) => setNewJobName(e.target.value)}
                  required
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Title"
                  helperText="User-facing display name"
                  value={newJobDisplayName}
                  onChange={(e) => setNewJobDisplayName(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <FormControlLabel
              value="existing"
              control={<Radio />}
              disabled={!currentJob}
              label={
                <Box>
                  <Typography variant="subtitle1">Import into the current job</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentJob ? `Job: ${currentJob}` : 'No job currently selected'}
                  </Typography>
                </Box>
              }
            />
            {destination === 'existing' && currentJob && (
              <RadioGroup
                value={existingMode}
                onChange={(e) => setExistingMode(e.target.value as 'add' | 'topup')}
                sx={{ mt: 1, ml: 4 }}
              >
                <FormControlLabel
                  value="add"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body1">Add variables</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Same respondents, extra variables
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="topup"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body1">Top-up</Typography>
                      <Typography variant="body2" color="text.secondary">
                        New respondents (a further wave) appended to the job
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            )}
          </CardContent>
        </Card>
      </RadioGroup>
    </Box>
  )

  const renderUploadStep = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        sx={{
          border: '2px dashed',
          borderColor: savFile ? 'success.main' : 'grey.400',
          bgcolor: savFile ? 'success.light' : 'grey.50',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light' },
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <input
            type="file"
            accept=".sav,.spss"
            onChange={(e) => e.target.files?.[0] && setSavFile(e.target.files[0])}
            style={{ display: 'none' }}
            id="sav-upload"
          />
          <label htmlFor="sav-upload" style={{ cursor: 'pointer', display: 'block' }}>
            {savFile ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {savFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(savFile.size)}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drop SPSS .sav file here or click to upload
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accepted formats: .sav, .spss
                </Typography>
              </>
            )}
          </label>
        </CardContent>
      </Card>

      <Card
        variant="outlined"
        sx={{
          border: '1px dashed',
          borderColor: blnFile ? 'success.main' : 'grey.300',
          bgcolor: blnFile ? 'success.light' : 'transparent',
        }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <input
            type="file"
            accept=".bln"
            onChange={(e) => e.target.files?.[0] && setBlnFile(e.target.files[0])}
            style={{ display: 'none' }}
            id="bln-upload"
          />
          <label htmlFor="bln-upload" style={{ cursor: 'pointer', flex: 1 }}>
            {blnFile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: 'success.main' }} />
                <Typography variant="body1">{blnFile.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  ({formatFileSize(blnFile.size)})
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1">Blend file (.bln) — optional</Typography>
                <Typography variant="body2" color="text.secondary">
                  Combines binary variables into multi-response variables
                </Typography>
              </Box>
            )}
          </label>
          {blnFile && (
            <Button size="small" color="info" onClick={() => setBlnFile(null)}>
              Remove
            </Button>
          )}
        </CardContent>
      </Card>

      {state.stage === 'inspecting' && (
        <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <CircularProgress size={18} color="info" />
              <Typography>Uploading and reading the file...</Typography>
            </Box>
            <LinearProgress color="info" sx={{ borderRadius: 1, height: 6 }} />
          </CardContent>
        </Card>
      )}
      {state.stage === 'idle' && state.error && (
        <Alert severity="error" data-testid="sav-import-upload-error">
          {state.error}
        </Alert>
      )}
    </Box>
  )

  const renderConflict = () => {
    if (!conflict) return null
    if (conflict.error === 'case_count_mismatch') {
      return (
        <Alert severity="error" data-testid="sav-import-conflict">
          <AlertTitle>Case counts differ</AlertTitle>
          The job has {conflict.jobCases} cases but the file has {conflict.fileCases}. Adding variables needs the same
          respondents in the same order. To append new respondents, go back and choose Top-up.
        </Alert>
      )
    }
    if (conflict.error === 'name_conflict') {
      const constructs = conflict.constructs ?? []
      const existing = conflict.existing ?? []
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {constructs.length > 0 && (
            <Alert severity="error" data-testid="sav-import-conflict">
              <AlertTitle>Constructed variables would be replaced</AlertTitle>
              {constructs.join(', ')} {constructs.length === 1 ? 'is a constructed variable' : 'are constructed variables'}{' '}
              in this job. An import never replaces them; rename or delete the construct first.
            </Alert>
          )}
          {existing.length > 0 && (
            <Alert severity="warning" data-testid="sav-import-conflict">
              <AlertTitle>Variables already in the job</AlertTitle>
              {existing.join(', ')}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="info"
                      checked={overwriteExisting}
                      onChange={(e) => setOverwriteExisting(e.target.checked)}
                    />
                  }
                  label="Overwrite existing variables"
                />
              </Box>
            </Alert>
          )}
        </Box>
      )
    }
    if (conflict.error === 'job_not_empty') {
      return (
        <Alert severity="error" data-testid="sav-import-conflict">
          The job {conflict.jobName} already has variables. Choose another ID, or import into it as Add variables or Top-up.
        </Alert>
      )
    }
    return (
      <Alert severity="error" data-testid="sav-import-conflict">
        The job {conflict.jobName} could not be found.
      </Alert>
    )
  }

  const renderVariableTable = (variables: SavPlannedVariable[], codesHeading: string) => (
    <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader data-testid="sav-import-variables">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Codes</TableCell>
            <TableCell>{codesHeading}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {variables.map((v) => {
            const strategy = STRATEGY_LABELS[v.codeStrategy] ?? { label: v.codeStrategy, title: v.codeStrategy }
            return (
              <TableRow key={v.sourceName}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{v.sourceName}</TableCell>
                <TableCell>{v.label}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {v.type}
                    {v.isBlended && <Chip size="small" variant="outlined" label="Blend" />}
                    {v.isWeight && <Chip size="small" color="info" label="Weight" />}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={strategy.label} title={strategy.title} />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{codesSummary(v)}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const renderReviewStep = () => {
    const plan = state.inspect
    if (!plan) return null
    const errors = plan.errors ?? []
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Import Summary
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">File</Typography>
                <Typography>{inspectedFiles?.sav.name}</Typography>
                {inspectedFiles?.bln && (
                  <Typography variant="body2" color="text.secondary">Blend: {inspectedFiles.bln.name}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Customer</Typography>
                <Typography data-testid="sav-import-summary-customer">{customerLabel}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Destination Job</Typography>
                <Typography>{jobName}</Typography>
                {mode === 'new' && newJobDisplayName && (
                  <Typography variant="body2" color="text.secondary">({newJobDisplayName})</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Cases</Typography>
                <Typography data-testid="sav-import-case-count">{plan.caseCount}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Variables</Typography>
                <Typography>{(plan.variables ?? []).length}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Weight</Typography>
                <Typography>{plan.weightVariable || 'None'}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {errors.length > 0 ? (
          <Alert severity="error" data-testid="sav-import-errors">
            <AlertTitle>This file cannot be imported</AlertTitle>
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </Alert>
        ) : (
          <Alert severity="info">{modeDescription}</Alert>
        )}

        <SavPlanMessages warnings={plan.warnings} notes={plan.notes} />

        {renderConflict()}

        {state.stage === 'reviewing' && state.error && (
          <Alert severity="error" data-testid="sav-import-run-error">
            {state.error}
          </Alert>
        )}

        {renderVariableTable(plan.variables ?? [], 'Codes in file')}
      </Box>
    )
  }

  const renderImportStep = () => {
    const progress = state.progress
    const plan = state.inspect
    const runMode = state.run?.mode ?? mode

    if (state.stage === 'running' || state.stage === 'starting') {
      const total = progress?.variablesTotal ?? 0
      return (
        <Card variant="outlined" sx={{ borderColor: 'info.main' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <CircularProgress size={18} color="info" />
              <Typography data-testid="sav-import-phase">{progress?.phaseLabel || 'Starting import...'}</Typography>
            </Box>
            <LinearProgress
              variant={total && progress?.variablesDone ? 'determinate' : 'indeterminate'}
              value={total ? ((progress?.variablesDone ?? 0) / total) * 100 : 0}
              color="info"
              sx={{ borderRadius: 1, height: 6 }}
            />
            {total ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {progress?.variablesDone ?? 0} of {total} variables
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              The import runs in the background. You can leave this page; the job's variable tree changes only when the
              import has finished.
            </Typography>
          </CardContent>
        </Card>
      )
    }

    const warnings = progress?.warnings ?? []
    const constructResults = progress?.constructResults
    // The data is written either way; a constructed variable that failed to
    // rebuild is short of the new cases, so every table on it is wrong until
    // it is rebuilt. That must not read as a green success.
    const failedConstructs = (constructResults ?? []).filter((r) => r.status === 'failed').length
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-testid="sav-import-report">
        {state.stage === 'completed' && failedConstructs > 0 ? (
          <Alert severity="error" data-testid="sav-import-complete-with-failures">
            <AlertTitle>Import complete, but {failedConstructs} constructed variable{failedConstructs === 1 ? '' : 's'} failed to rebuild</AlertTitle>
            {progress?.variablesDone ?? 0} variables written to {state.run?.jobName}
            {plan ? ` from ${plan.caseCount} cases` : ''}. The failed variables below are out of date until they are rebuilt.
          </Alert>
        ) : state.stage === 'completed' ? (
          <Alert severity="success" data-testid="sav-import-complete">
            <AlertTitle>Import complete</AlertTitle>
            {progress?.variablesDone ?? 0} variables written to {state.run?.jobName}
            {plan ? ` from ${plan.caseCount} cases` : ''}.
          </Alert>
        ) : (
          <Alert severity="error" data-testid="sav-import-failed">
            <AlertTitle>Import failed</AlertTitle>
            {state.error}
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning" data-testid="sav-import-report-warnings">
            <AlertTitle>Warnings</AlertTitle>
            {warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </Alert>
        )}

        {constructResults && constructResults.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Constructed variables rebuilt
            </Typography>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" data-testid="sav-import-construct-results">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {constructResults.map((r) => (
                    <TableRow key={r.name}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.name}</TableCell>
                      <TableCell>{r.kind === 'dataFile' ? 'From data file' : r.kind === 'user' ? 'User construct' : r.kind}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell sx={{ color: 'error.main' }}>{r.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {state.stage === 'completed' && plan && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Variables
            </Typography>
            {renderVariableTable(plan.variables ?? [], runMode === 'topup' ? 'Codes in file' : 'Codes added')}
          </Box>
        )}
      </Box>
    )
  }

  const renderActions = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button variant="contained" color="info" onClick={() => setActiveStep(1)} disabled={!canProceedJob}>
            Next
          </Button>
        )
      case 1:
        return (
          <Button
            variant="contained"
            color="info"
            onClick={handleInspect}
            disabled={!savFile || state.stage === 'inspecting'}
            startIcon={state.stage === 'inspecting' ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {inspectCurrent && state.stage === 'reviewing' ? 'Next' : 'Upload & review'}
          </Button>
        )
      case 2:
        return (
          <Button
            variant="contained"
            color="info"
            startIcon={state.stage === 'starting' ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleImport}
            disabled={!canImport}
          >
            Import
          </Button>
        )
      default:
        if (state.stage === 'completed') {
          return (
            <>
              <Button variant="outlined" color="info" onClick={handleStartAgain}>
                Import another file
              </Button>
              <Button variant="contained" color="info" onClick={handleOpenVariables}>
                Open Variables
              </Button>
            </>
          )
        }
        if (state.stage === 'failed') {
          return (
            <Button variant="contained" color="info" onClick={handleStartAgain}>
              Start again
            </Button>
          )
        }
        return null
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 'calc(100vh - 84px)', p: 3 }}>
      <Paper elevation={1} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} color="info" onClick={handleBack} disabled={activeStep === 3}>
            Back
          </Button>
          <Typography variant="h5">Import Variables</Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 300 }}>
          {activeStep === 0 && renderJobStep()}
          {activeStep === 1 && renderUploadStep()}
          {activeStep === 2 && renderReviewStep()}
          {activeStep === 3 && renderImportStep()}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          {renderActions()}
        </Box>
      </Paper>
    </Box>
  )
}

export default SavImportPage
